import React from 'react'
import Blob from '@/components/Blob'
import Illustration from '@/components/Illustration'
import Card from '@/components/ui/Card'

const sections = [
  {
    id: 'problem',
    title: 'The Problem',
    intro:
      'Classrooms need flexible funding to keep pace with what their students are dreaming up, yet educators often shoulder the financial gap themselves.',
    bullets: [
      'Traditional budgets rarely stretch to cover enrichment, emerging technology, or the supplies that make ideas tangible.',
      'Fundraising takes precious time away from teaching, and piecemeal efforts make it hard for communities to stay engaged.',
      'Sponsors want visibility into impact but rarely see beyond a single thank-you note or spreadsheet update.',
    ],
    illustration: 'donut',
  },
  {
    id: 'approach',
    title: 'Our Approach',
    intro:
      'Project Tower pairs direct classroom sponsorship with a steady drumbeat of storytelling so every partner can see the difference their support makes.',
    bullets: [
      'Teachers publish programs with specific goals, milestones, and materials their students need most.',
      'Sponsors back classrooms instantly and receive guided updates, photos, and receipts along the way.',
      'Our team handles payouts, compliance, and gratitude touches so relationships stay strong and focused on learning.',
    ],
    illustration: 'teacher',
  },
  {
    id: 'success',
    title: 'What Success Looks Like',
    intro:
      'We celebrate progress with concrete outcomes that signal classrooms are resourced, students are thriving, and partners return year after year.',
    bullets: [
      'Learning experiences are launched without delay, with real-time updates that highlight student voices.',
      'Teachers reclaim hours each month to focus on instruction instead of fundraising logistics.',
      'Sponsors see transparent impact metrics that inspire renewed commitments and introductions to new partners.',
    ],
    illustration: 'kids',
  },
]

const principles = [
  'Lead with transparency and timely communication.',
  'Center teacher voice in every decision and deliverable.',
  'Design for equity so resources reach classrooms with the greatest needs.',
  'Measure outcomes that reflect both academic and social-emotional growth.',
  'Honor sponsor intent with clear budgeting and stewardship.',
  'Celebrate wins publicly to inspire the next wave of supporters.',
]

export default function Mission() {
  return (
    <div className="space-y-12">
      <section className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-shell via-white to-shell px-6 py-12 shadow-soft">
        <Blob className="absolute -top-20 -left-10 h-56 w-56 opacity-60" />
        <Blob className="absolute -bottom-24 -right-16 h-64 w-64 opacity-40" />
        <div className="relative z-10 flex flex-col gap-8 md:flex-row md:items-center">
          <div className="max-w-3xl space-y-5">
            <span className="inline-flex items-center rounded-full bg-white/70 px-3 py-1 text-xs font-semibold uppercase tracking-wide text-cocoa/70">
              Our Mission
            </span>
            <h1 className="text-4xl font-semibold tracking-tight text-cocoa">Every student deserves a launchpad.</h1>
            <p className="text-lg leading-relaxed text-cocoa/80">
              We partner with teachers, sponsors, and communities to remove friction between imagination and opportunity.
              By stewarding resources and storytelling with heart, Project Tower elevates classrooms so ideas can reach new
              heights.
            </p>
          </div>
          <Illustration name="books" animate className="mx-auto h-36 w-36 md:h-48 md:w-48" />
        </div>
      </section>

      <section className="grid gap-6 lg:grid-cols-3">
        {sections.map((section) => (
          <Card
            as="article"
            key={section.id}
            className="relative flex h-full flex-col gap-4 overflow-hidden"
          >
            <Blob className="absolute -top-16 -right-12 h-44 w-44 opacity-40" />
            <div className="relative z-10 flex items-start justify-between gap-4">
              <header className="space-y-2">
                <h2 className="text-2xl font-semibold text-cocoa">{section.title}</h2>
                <p className="text-sm leading-relaxed text-cocoa/80">{section.intro}</p>
              </header>
              <Illustration name={section.illustration} className="h-16 w-16" animate />
            </div>
            <ul className="relative z-10 space-y-3 text-sm text-cocoa/80">
              {section.bullets.map((bullet) => (
                <li key={bullet} className="flex gap-2">
                  <span className="mt-1 inline-block h-2 w-2 flex-shrink-0 translate-y-1 rounded-full bg-brick" aria-hidden />
                  <span>{bullet}</span>
                </li>
              ))}
            </ul>
          </Card>
        ))}
      </section>

      <section className="grid gap-6 lg:grid-cols-[2fr,1fr]">
        <Card className="relative overflow-hidden">
          <Blob className="absolute -top-20 -left-16 h-48 w-48 opacity-40" />
          <div className="relative z-10 space-y-5">
            <h2 className="text-2xl font-semibold text-cocoa">Our Principles</h2>
            <p className="text-sm text-cocoa/80">
              These commitments ground every partnership we build and every classroom story we champion.
            </p>
            <ul className="grid gap-4 sm:grid-cols-2">
              {principles.map((principle) => (
                <li key={principle} className="flex items-start gap-3 text-sm text-cocoa/80">
                  <span
                    className="mt-1 flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full bg-mint/20 text-mint"
                    aria-hidden
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      viewBox="0 0 20 20"
                      fill="currentColor"
                      className="h-4 w-4"
                    >
                      <path
                        fillRule="evenodd"
                        d="M16.704 4.153a.75.75 0 0 1 .143 1.052l-7 9a.75.75 0 0 1-1.127.075l-3.5-3.5a.75.75 0 0 1 1.06-1.06l2.894 2.893 6.48-8.325a.75.75 0 0 1 1.05-.135Z"
                        clipRule="evenodd"
                      />
                    </svg>
                  </span>
                  <span>{principle}</span>
                </li>
              ))}
            </ul>
          </div>
        </Card>

        <Card className="relative overflow-hidden bg-gradient-to-br from-shell via-white to-shell text-cocoa">
          <Blob className="absolute -bottom-12 -right-14 h-48 w-48 opacity-40" />
          <div className="relative z-10 flex h-full flex-col justify-between gap-6">
            <Illustration name="leaf" animate className="h-16 w-16 md:h-20 md:w-20" />
            <blockquote className="space-y-4">
              <p className="text-lg italic leading-relaxed">
                “When classrooms have what they need, students walk in with confidence and leave ready to change the world.”
              </p>
              <footer className="text-sm font-medium text-cocoa/80">— Project Tower Partner Teacher</footer>
            </blockquote>
          </div>
        </Card>
      </section>
    </div>
  )
}
